import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { Table, Row, Popover } from 'antd';
import RiskIndicator from '../../../Common/RiskIndicator/RiskIndicator';
import Alert from '../../../Common/Alert/Alert';

import { formatDate, formatDecimal, getSymbolCurrency } from '../../../../helpers/format';
import { productServices } from "../../../../api/services/products";
import 'react-calendar/dist/Calendar.css';

const baseURLBackend = 'https://www.santanderassetmanagement.es/';
const PAGE_SIZE = 20;
const PREFETCH_THRESHOLD = 200;

const InfiniteTable = ({ filterNumber, setSelectedProduct, startComparing, openHistorico, selectProducto, tableData }) => {
    const [showAlert, setShowAlert] = useState(false);
    const [displayFundsList, setDisplayFundsList] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const nextItemIndex = useRef(0);
    const countRef = useRef(filterNumber);
    const loadingRef = useRef(false);
    const sentinelRef = useRef(null);
    const animationFrameRef = useRef(null);
    const navigate = useNavigate();
    // Ajusta a tu proyecto
    const baseURLBackendGestor = window._env_.BACKEND_GESTOR;
    const ALLOWED_HOSTS = new Set([
        'www.santanderassetmanagement.es',
        'www.portalprivado.santanderassetmanagement.es'
    ]);
    const ALLOWED_PROTOCOLS = new Set(['https:']);

    function safeResolve(urlLike, base) {
        try {
            const u = new URL(urlLike, base);

            if (!ALLOWED_PROTOCOLS.has(u.protocol)) return null;         // no http:, javascript:, data:, etc.
            if (!ALLOWED_HOSTS.has(u.hostname)) return null;             // allow-list de hosts
            //if (/[^\x20-\x7E]/.test(urlLike)) return null;               // rechaza control chars
            if (/%0d|%0a/i.test(u.href)) return null;                    // evita CRLF injection

            return u.toString();
        } catch {
            return null;
        }
    }
    const detectBase = (url) => url.startsWith('/documents/')
        ? baseURLBackendGestor
        : baseURLBackend;

    const getRowOptionsHTML = (productInfo) => {
        const getTypeProductUrl = async (item) => {
            try {
                let response;
                item.isin
                    ? (response = await productServices.getProductDetail(item.isin))
                    : (response = await productServices.getProductDetail(item.codigoDgs));
                const { documentacion } = response.response;
                const datosFundamentales = documentacion.filter(
                    (doc) => doc.txdesdoc.toLowerCase() === "pdffolletoexplicativo" || doc.txdesdoc.toLowerCase() === "pdfdocumentofundamental"
                );

                if (!datosFundamentales.length) {
                    setShowAlert(true);
                    return;
                }
                const cruda = datosFundamentales.length > 0 ? (datosFundamentales[0]?.urlcompleta ?? '') : '';
                const base = detectBase(cruda);
                const segura = safeResolve(cruda, base);

                if (segura) {
                    const w = window.open(segura, '_blank', 'noopener,noreferrer');
                    if (w) w.location.assign(segura);
                    return true;
                } else {
                    setShowAlert(true);
                    return false;
                }
            } catch (e) {
                console.error(e);
                setShowAlert(true);
                return null;
            }
        };
        const getTypeInitials = (item) => {
            let typeName = "";
            const productType = item ? item.tipo : null;
            if (productType) {
                const mapedType = {
                    planes: "DDFP",
                    planesindiv: "DDFP",
                    planesasoc: "DDFP",
                    fondos: "DDF",
                    epvs: "DIP",
                    sicav: "KIID",
                };

                typeName = mapedType[productType] || "";
            }

            return typeName;
        };

        const tableOptions = [
            {
                label: 'Ver detalle', action: (item) => {
                    selectProducto(item);
                    navigate(`/id/${item.isin ? item.isin : item.codigoDgs}`);
                    // setSelectedProduct(item);
                }
            },
            {
                label: 'Comparar', action: (item) => {
                    startComparing();
                    navigate(`/compare/${item.isin ? item.isin : item.codigoDgs}`);
                    // setSelectedProduct(item);
                }
            },
            ({
                label: getTypeInitials(productInfo), action: (item) => {
                    getTypeProductUrl(item);
                }
            }),
            {
                label: 'Histórico', action: (item) => {
                    openHistorico(item);
                }
            }
        ];
        return tableOptions;
    }

    const columns = [
        {
            title: 'Producto',
            dataIndex: 'producto',
            key: 'producto',
            sorterOrder: 'descend',
            sorter: (a, b) => a.nombre.toLowerCase() > b.nombre.toLowerCase(),
            render: (text, record, idx) =>
                <div
                    data-testid="producto-tabla-buscador"
                    className='producto-movil product-title d-flex'
                // onClick={(e) => { setSelectedProduct(record); selectProducto(record, e);}}
                >
                    <div className='flex-grow-1'>
                        <span className='product-title-name'>
                            <Link to={`/id/${record.isin ? record.isin : record.codigoDgs}`}>{record.nombre}</Link>
                        </span>
                        <div className='d-flex'>
                            <span className='product-title-details'>
                                {(record.isin) ? (`${record.isin} | ${record.descri_categoria} | ${formatDate(record.fechaUltimaValoracion, 'dd-MM-yyyy')}`) : (`${record.codigoDgs} | ${record.descri_categoria} | ${formatDate(record.fechaUltimaValoracion, 'dd-MM-yyyy')}`)}
                            </span>
                        </div>
                    </div>
                </div>
        },
        {
            title: 'Valor liquid.',
            dataIndex: 'valorL',
            key: 'valorL',
            sorter: (a, b) => a.valorParticipacion - b.valorParticipacion,
            render: (text, record, idx) =>
                <div style={{ textAlign: 'right' }} onClick={() => setSelectedProduct(record)}>
                    {formatDecimal(record.valorParticipacion, 5)}
                </div>
        },
        {
            title: 'Patrimonio',
            dataIndex: 'patrimonio',
            key: 'patrimonio',
            sorter: (a, b) => a.imvrfond - b.imvrfond,
            render: (text, record, idx) =>
                <div style={{ textAlign: 'right' }} onClick={() => setSelectedProduct(record)}>
                    {
                        (() => {
                            if (!record.imvrfond) return '-';
                            //INC049394411 - El servicio nos devuelve planes y epsvs en M€ y fondos/sicavs en €
                            if (record.tipo === "epvs" || record.tipo === "planes") {
                                if (record.imvrfond >= 1) {
                                    return formatDecimal(record.imvrfond) + " M" + getSymbolCurrency(record.divisa);
                                } else if (record.imvrfond <= 1) {
                                    return formatDecimal(record.imvrfond) + " K" + getSymbolCurrency(record.divisa);
                                }
                            } else {
                                if (record.imvrfond >= 100000) {
                                    return formatDecimal(record.imvrfond / 1000000) + " M" + getSymbolCurrency(record.divisa);
                                } else if (record.imvrfond >= 1000 && record.imvrfond < 100000) {
                                    return formatDecimal(record.imvrfond / 1000) + " k" + getSymbolCurrency(record.divisa);
                                } else {
                                    return formatDecimal(record.imvrfond) + getSymbolCurrency(record.divisa);
                                }
                            }
                        })()
                    }
                </div>
        },
        {
            title: 'Inversión mín.',
            dataIndex: 'inversionM',
            key: 'inversionM',
            sorter: (a, b) => a.inversionMinima.match(/\d/g).join("") - b.inversionMinima.match(/\d/g).join(""),
            render: (text, record, idx) =>
                <div style={{ textAlign: 'right' }} onClick={() => setSelectedProduct(record)}>
                    {record.inversionMinima}
                </div>
        },
        {
            title: 'Riesgo',
            dataIndex: 'riesgo',
            key: 'riesgo',
            sorter: (a, b) => a.riesgo - b.riesgo,
            render: (text, record, idx) =>
                <span style={{ textAlign: 'right' }} onClick={() => setSelectedProduct(record)}>
                    <RiskIndicator riskLevel={record.riesgo} productType={record.tipo} />
                </span>
        },
        {
            title: 'Rentabilidades',
            dataIndex: 'rentabilidades',
            key: 'rentabilidades',
            children: [
                {
                    title: 'Mes',
                    dataIndex: 'mes',
                    key: 'mes',
                    sorter: (a, b) => a.mes - b.mes,
                    render: (text, record, idx) =>
                        <div style={{ textAlign: 'center' }} onClick={() => setSelectedProduct(record)}>
                            {record.mes ?
                                formatDecimal(record.mes) + ' %' :
                                '-'
                            }
                        </div>
                },
                {
                    title: 'YTD',
                    dataIndex: 'ytd',
                    key: 'ytd',
                    sorter: (a, b) => a.ytd - b.ytd,
                    render: (text, record, idx) =>
                        <div style={{ textAlign: 'center' }} onClick={() => setSelectedProduct(record)}>
                            {record.ytd ?
                                formatDecimal(record.ytd) + ' %' :
                                '-'
                            }
                        </div>
                },
                {
                    title: '1 año',
                    dataIndex: '1year',
                    key: '1year',
                    sorter: (a, b) => a.uno - b.uno,
                    render: (text, record, idx) =>
                        <div style={{ textAlign: 'center' }} onClick={() => setSelectedProduct(record)}>
                            {record.uno ?
                                formatDecimal(record.uno) + ' %' :
                                '-'
                            }
                        </div>
                },
                {
                    title: '3 años',
                    dataIndex: '3years',
                    key: '3years',
                    sorter: (a, b) => a.tres - b.tres,
                    render: (text, record, idx) =>
                        <div style={{ textAlign: 'center' }} onClick={() => setSelectedProduct(record)}>
                            {record.tres ?
                                formatDecimal(record.tres) + ' %' :
                                '-'
                            }
                        </div>
                },
                {
                    title: '5 años',
                    dataIndex: '5years',
                    key: '5years',
                    sorter: (a, b) => a.cinco - b.cinco,
                    render: (text, record, idx) =>
                        <div style={{ textAlign: 'center' }} onClick={() => setSelectedProduct(record)}>
                            {record.cinco ?
                                formatDecimal(record.cinco) + ' %' :
                                '-'
                            }
                        </div>
                }
            ]
        },
        {
            title: '',
            dataIndex: 'options',
            key: 'options',
            className: "options-column",
            width: 50,
            onCell: () => {
                return {
                    onClick: (event) => {
                        event.stopPropagation();
                    },
                };
            },
            render: (text, record, idx) =>
                <span className='tableOptionsIcon'>
                    <Popover
                        overlayStyle={{ maxWidth: 150 }}
                        title={null}
                        placement='bottom'
                        content={
                            <div style={{ color: '#005670', fontWeight: 500 }}>
                                {
                                    getRowOptionsHTML(record).map((e, idx) =>
                                        <div key={"table-option-" + idx} style={{ cursor: 'pointer', marginBottom: 5 }} onClick={() => e.action(record)}>
                                            {e.label}
                                        </div>)
                                }
                            </div>}
                    >
                        {' '}
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path fill="red" d="M320 208C289.1 208 264 182.9 264 152C264 121.1 289.1 96 320 96C350.9 96 376 121.1 376 152C376 182.9 350.9 208 320 208zM320 432C350.9 432 376 457.1 376 488C376 518.9 350.9 544 320 544C289.1 544 264 518.9 264 488C264 457.1 289.1 432 320 432zM376 320C376 350.9 350.9 376 320 376C289.1 376 264 350.9 264 320C264 289.1 289.1 264 320 264C350.9 264 376 289.1 376 320z"/></svg>
                    </Popover>
                </span>
        }
    ];

    const loadMoreData = useCallback(() => {
        if (loadingRef.current || tableData.length < 1) {
            return;
        }
        const isNewSearch = filterNumber !== countRef.current;
        const startIndex = isNewSearch ? 0 : nextItemIndex.current;
        const nextPage = tableData.slice(startIndex, startIndex + PAGE_SIZE);

        if (nextPage.length < 1) {
            return;
        }

        loadingRef.current = true;
        setLoadingData(true);
        countRef.current = filterNumber;
        nextItemIndex.current = startIndex + nextPage.length;

        if (isNewSearch || displayFundsList.length < 1) {
            setDisplayFundsList(nextPage);
        } else {
            setDisplayFundsList((currentList) => [...currentList, ...nextPage]);
        }

        loadingRef.current = false;
        setLoadingData(false);
    }, [tableData, filterNumber, displayFundsList.length]);

    const checkSentinel = useCallback(() => {
        if (animationFrameRef.current !== null) {
            return;
        }

        const requestFrame = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
        animationFrameRef.current = requestFrame(() => {
            animationFrameRef.current = null;
            const sentinel = sentinelRef.current;

            if (
                !sentinel ||
                displayFundsList.length >= tableData.length ||
                sentinel.getBoundingClientRect().top > window.innerHeight + PREFETCH_THRESHOLD
            ) {
                return;
            }

            loadMoreData();
        });
    }, [displayFundsList.length, loadMoreData, tableData.length]);

    useEffect(() => {
        const scrollOptions = { capture: true, passive: true };
        window.addEventListener('scroll', checkSentinel, scrollOptions);
        window.addEventListener('resize', checkSentinel, { passive: true });
        checkSentinel();

        return () => {
            window.removeEventListener('scroll', checkSentinel, true);
            window.removeEventListener('resize', checkSentinel);

            if (animationFrameRef.current !== null) {
                const cancelFrame = window.cancelAnimationFrame || window.clearTimeout;
                cancelFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [checkSentinel]);

    const hasMore = displayFundsList.length < tableData.length;

    return (
        <>
            {showAlert && (
                <Alert
                    type="error"
                    message="No existe el recurso solicitado para este registro"
                    onClose={() => setShowAlert(false)}
                    duration={4000}
                />
            )}
            <Table
                showSorterTooltip={{ title: 'Clic para ordenar' }}
                pagination={false}
                columns={columns}
                dataSource={displayFundsList}
                rowKey={(record) => (record?.isin) ? (record?.isin) : (record?.codigoDgs)}
                rowClassName="product-row"
                onRow={(record) => {
                    return {
                        onClick: () => { navigate(`/id/${record.isin ? record.isin : record.codigoDgs}`) }
                    }
                }}
            />
            <div ref={sentinelRef}>
                {loadingData && hasMore && (
                    <Row justify="center"><Spinner animation="border" variant="danger" role="status" /></Row>
                )}
            </div>
        </>
    )
};

export default InfiniteTable;